import type { ReactNode } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import type { TeamMemberData } from '@launchpad/components/common/TeamMemberCard';
import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import type { ProjectContentTableOfContentTab } from '../types';

const highlights = [
  'Advanced Trading Opportunity: Leverage up to x50, instant price updates, crypto, commodity, stock and forex trading pairs with collateral in TON and USDT.',
  "High Yield Liquidity Provision: Single-token liquidity pools earning 70% of the protocol's fees with high annual returns.",
  'Social-Fi Mechanics: Direct integration in Telegram, squads trading tournaments, NFT collections, copytrading and other viral mechanics for better community engagement.',
  'User-friendly interface: Familiar interface in the best traditions of trading DEXes with TradingView graph.',
  'Security: Full decentralisation and advanced security protocols to keep your funds safe.',
];

const productProblems = [
  "DEX'es are hard to onboard; in futures they only have 1.5% of volume.",
  "Many traders get their trading knowledge from the KOL's at TG. This usually takes a lot of time and can be challenging to connect to the exchange and start trading.",
];

const productSolutions = [
  'Together with Telegram integration Storm Trade makes access to trading as easy as possible, making trading not boring through social-fi mechanics.',
  'Storm Trade streamlines and speeds up the process of going from KOL’s post on Telegram to opening an exchange and starting trading immediately.',
];

const marketingStrategies = [
  'Go-to-market strategy consists of emphasising the revenue sharing mechanics of the protocol, partnering with KOLs, PR marketing, community activities in the whitelist campaign and distribution of the unique NFT collection with strong utility.',
  'KOLs: Daan Crypto Trades, Hanzo, Joker, TraderLenny, 0xTodd, EllioTrades, and other Tier1 KOLs.',
];

const teamMembers: TeamMemberData[] = [
  {
    id: 'andrew',
    fullName: 'Andrew',
    about: 'Optimising trading and DeFi strategies from 2020 with 30%+ APR.',
    position: 'COO',
    width: 104,
    height: 104,
    avatar: '/launchpad/stories/st/storm-trade-team-andrew.png',
  },
  {
    id: 'slava',
    fullName: 'Slava',
    about: 'A proficient smart contract developer with multiple TON Contests awards.',
    position: 'Dev',
    width: 104,
    height: 104,
    avatar: '/launchpad/stories/st/storm-trade-team-slava.png',
  },
  {
    id: 'tim',
    fullName: 'Tim',
    about:
      'ex. CTO at perps startup Tsunami.exchange, over 5 years exp. technical leading DeFi projects.',
    position: 'CTO',
    width: 104,
    height: 104,
    avatar: '/launchpad/stories/st/storm-trade-team-tim.png',
  },
  {
    id: 'denis',
    fullName: 'Denis',
    about:
      'in crypto since 2019, ex. DeFi development studio funder, 15+ projects for clients, MBA degree.',
    position: 'CEO',
    width: 104,
    height: 104,
    avatar: '/launchpad/stories/st/storm-trade-team-denis.png',
  },
];

const kols = [
  {
    name: '0xTodd',
    avatar: '/launchpad/stories/st/storm-trade-kol-todd.png',
  },
  {
    name: 'Hanzo',
    avatar: '/launchpad/stories/st/storm-trade-kol-hanzo.png',
  },
  {
    name: 'Joker',
    avatar: '/launchpad/stories/st/storm-trade-kol-joker.png',
  },
  {
    name: 'Daan Crypto\u00A0Trades',
    avatar: '/launchpad/stories/st/storm-trade-kol-daan.png',
  },
  {
    name: 'TraderLenny',
    avatar: '/launchpad/stories/st/storm-trade-kol-lenny.png',
  },
  {
    name: 'EllioTrades',
    avatar: '/launchpad/stories/st/storm-trade-kol-ellio.png',
  },
];

const partners = [
  {
    name: 'Cointelegraph',
    image: '/launchpad/stories/st/storm-trade-partner-cointelegraph-2.png',
    width: 120,
    height: 28,
  },
  {
    name: 'Tonstarter',
    image: '/launchpad/stories/st/storm-trade-partner-tonstarter-2.png',
    width: 116,
    height: 33,
  },
  {
    name: 'Gotbit Hedge Fund',
    image: '/launchpad/stories/st/storm-trade-partner-gotbit-2.png',
    width: 137,
    height: 22,
  },
  {
    name: 'PYTH',
    image: '/launchpad/stories/st/storm-trade-partner-pyth-2.png',
    width: 108,
    height: 37,
  },
];

const investors = [
  {
    image: '/launchpad/stories/st/storm-trade-investor-toncoin-fund.png',
    name: 'TONcoin Fund',
    width: 109,
    height: 10,
  },
  {
    name: 'Yolo Investment',
    image: '/launchpad/stories/st/storm-trade-investor-yolo.png',
    width: 98,
    height: 46,
  },
  {
    name: 'Blackdragon',
    image: '/launchpad/stories/st/storm-trade-investor-blackdragon.png',
    layout: 'compact',
    width: 72,
    height: 72,
  },
  {
    name: 'Sky9 Capital',
    image: '/launchpad/stories/st/storm-trade-investor-sky9.png',
    layout: 'compact',
    width: 72,
    height: 72,
  },
  {
    name: 'CSPDAO',
    image: '/launchpad/stories/st/storm-trade-investor-cspdao.png',
    width: 103,
    height: 27,
  },
  {
    name: 'JRRCrypto',
    image: '/launchpad/stories/st/storm-trade-investor-jrr-crypto.png',
    width: 95,
    height: 27,
  },
];

const tokenUtilities = [
  'Staking. All token stakers will receive a part of the trading fees collected. Fees on Storm Trade are taken when opening and closing a position, funding payments, rollover payments and liquidations. It turns out that the amount of fees is directly correlated with the daily volume of trades in the protocol.',
  'Rewarding. Tokens will be used as rewards for winning trade tournaments, participating in squads, inviting friends and more.',
  'Product. Token is used in PoS Consensus of Decentralized Execution Network (to be built in future).',
];

const productionSolutionGallery = [
  {
    image: '/launchpad/stories/st/storm-trade-product-solution-gallery-1.png',
  },
  {
    image: '/launchpad/stories/st/storm-trade-product-solution-gallery-2.png',
  },
  {
    image: '/launchpad/stories/st/storm-trade-product-solution-gallery-3.png',
  },
];

const SubTitle = ({ children, className }: { children: ReactNode; className?: string }) => {
  return (
    <h3 className={classNames('text-2xl font-semibold text-neutral-500 ', className)}>
      {children}
    </h3>
  );
};

const List = ({ items, className }: { items: string[]; className?: string }) => {
  return (
    <ul className={classNames('list-disc pl-4', className)}>
      {items.map((highlight, i) => {
        return <li key={i}>{highlight}</li>;
      })}
    </ul>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction and Highlights',
    content: (
      <>
        <SubTitle className="mb-4">Introduction</SubTitle>
        <p className="mb-6">
          Storm Trade is the first social-fi derivatives platform on Telegram, built on the TON
          blockchain, enables trading with up to x50 leverage. Backed by the TON Foundation and
          incubated by Tonstarter, Storm is poised to onboard the next 10 million DeFi users.
        </p>
        <SubTitle className="mb-4">Key Highlights</SubTitle>

        <List items={highlights} />
      </>
    ),
  },
  {
    id: 'product',
    title: 'Product',
    content: (
      <>
        <p className="mb-6">
          Social-first derivatives platform with up to x50 leverage on Telegram.
        </p>
        <SubTitle className="mb-4">Problem</SubTitle>

        <List items={productProblems} className="mb-6" />

        <SubTitle className="mb-4">Solution</SubTitle>

        <List items={productSolutions} className="mb-6" />

        <div className="overflow-hidden -mb-8">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {productionSolutionGallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-full md:w-3/4 px-1')}>
                <ImageProxy
                  alt=""
                  width={1920}
                  height={1080}
                  quality={80}
                  className="w-full h-auto rounded-lg select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    content: (
      <p>
        {`The business model of $STORM token is centred around the protocol's revenue sharing
        function. 30% of all fees collected from trading will be distributed to all token stakers.
        The higher the trading volume of the protocol, the higher the profit received by the token's
        stakeholders.`}
      </p>
    ),
  },
  {
    id: 'investors',
    title: 'Investors',
    content: (
      <>
        <p className="mb-4">
          TON Ventures (former TONCoin.fund - leading ecosystem VC in TON), Sky9 Capital,
          Blackdragon, JRR Capital, CSP DAO, Yolo Investments, Gotbit, and other Tier1 Investors.
        </p>

        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-1.5">
          {investors.map(({ name, image, width, height, layout }) => {
            return (
              <li
                key={name}
                className={classNames(
                  'h-[4.5rem] rounded-lg border ${padding} border-primary-200',
                  {
                    'p-3': layout !== 'compact',
                  },
                )}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'Partners',
    title: 'Partners',
    content: (
      <>
        <p className="mb-4">
          Cointelegraph, Gotbit, Toncoin.Fund, Pyth Network, Tonstarter, and other Tier1 Partners.
        </p>

        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-1.5 mb-10">
          {partners.map(({ name, image, width, height }) => {
            return (
              <li key={name} className="h-[4.5rem] p-5 rounded-lg border border-primary-200">
                <ImageProxy
                  alt={name}
                  width={242}
                  height={width}
                  quality={height}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>

        <SubTitle className="mb-6">Marketing Strategy and Key Opinion Leaders (KOLs)</SubTitle>

        <List items={marketingStrategies} className="mb-4" />

        <ul className="flex flex-wrap justify-around items-start gap-8">
          {kols.map(({ name, avatar }) => {
            return (
              <li key={name} className="flex justify-center">
                <span className="flex flex-col gap-2.5 w-[6.25rem] text-center font-semibold">
                  <ImageProxy
                    alt={`${name}'s avatar`}
                    className="w-full bg-primary-50 rounded-full"
                    src={avatar}
                    quality={80}
                    width={100}
                    height={100}
                  />
                  {name}
                </span>
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <p className="mb-4">
          The Storm team is made up of twelve skilled members with a wide range of expertise with 4
          core contributors:
        </p>

        <TeamMemberList teamMembers={teamMembers} />
      </>
    ),
  },
  {
    id: 'tokenomics',
    title: 'Tokenomics',
    content: (
      <ImageProxy
        alt="Tokenomics diagram: 33.8% Marketing, 36.2% Investors, 15.0% Team, 10.0% Liquidity, 5.0% Advisors, 1,000,000,000 Total $STORM Supply"
        src="/launchpad/stories/st/storm-trade-tokenomics.png"
        className="w-full h-auto rounded-lg shadow-card"
        quality={80}
        width={1547}
        height={567}
      />
    ),
  },
  {
    id: 'token-utility',
    title: 'Token Utility',
    content: (
      <>
        <List items={tokenUtilities} className="mb-4" />
        <p>
          The deflationary mechanics of the token will be based on locking tokens when they are sent
          to staking. Staking will reduce the number of tokens on the market, offering high returns
          from trading fees collected by the protocol.
        </p>
      </>
    ),
  },
];
